/**
 * Extruding building footprints.
 *
 * Emits geometry whose Y is relative to each building's own base, plus the
 * ranges needed to shift each one onto the ground later. Walls carry their own
 * duplicated vertices so that per-face normals come out flat rather than
 * smeared around every corner.
 */

import earcut from 'earcut';
import { SLOT_ROOF, SLOT_WALL } from './housepalette.js';

export interface BuildingFootprint {
  /** Outer ring then holes, each a flat list of x,z pairs. */
  rings: Float64Array[];
  height: number;
}

export interface BuildingGeometry {
  /** x, y, z triples. Y is relative to each building's base. */
  positions: Float32Array;
  indices: Uint32Array;
  /**
   * [vertexStart, vertexCount] per input footprint, for grounding each
   * separately. One pair per footprint, empty for a degenerate one.
   */
  ranges: Uint32Array;
  /**
   * Palette slot per vertex, so a caller can paint a shell the same way it
   * paints a generated house. Emitted here rather than inferred afterwards
   * because only this function knows which vertices are roof and which are
   * wall — from the outside the two are indistinguishable, since a wall's top
   * vertices sit at exactly the roof's height.
   */
  slots: Uint8Array;
  /** Per-vertex shade multiplier, 0..255. See `houses.ts`. */
  shades: Uint8Array;
}

/** Shade at the foot and the head of a wall. Matches `houses.ts`. */
const SHADE_WALL_BASE = 182;
const SHADE_WALL_TOP = 252;
const SHADE_FULL = 255;

/**
 * How far to sink each building below its lowest footprint corner.
 *
 * Buildings are grounded at the lowest terrain sample under their outline. On a
 * slope the uphill side would otherwise sit exactly on the surface, and any
 * terrain noise would open a gap under the wall. Burying the base slightly
 * costs nothing and guarantees the building meets the ground all the way round.
 */
export const BUILDING_EMBED = 1.2;

/**
 * Signed area of a ring in the x/z ground plane, by the shoelace formula.
 * Positive and negative describe opposite windings; which is which does not
 * matter, only that they are consistent.
 */
export function signedRingArea(ring: Float64Array): number {
  let sum = 0;
  const count = ring.length / 2;
  for (let i = 0; i < count; i++) {
    const j = (i + 1) % count;
    sum += ring[i * 2] * ring[j * 2 + 1] - ring[j * 2] * ring[i * 2 + 1];
  }
  return sum / 2;
}

function reversed(ring: Float64Array): Float64Array {
  const count = ring.length / 2;
  const out = new Float64Array(ring.length);
  for (let i = 0; i < count; i++) {
    out[i * 2] = ring[(count - 1 - i) * 2];
    out[i * 2 + 1] = ring[(count - 1 - i) * 2 + 1];
  }
  return out;
}

/**
 * Force the outer ring to positive winding and every hole to negative.
 *
 * Wall geometry is built by walking each ring and extruding its edges, so the
 * outward direction is entirely determined by which way round the ring goes.
 * Rather than trust the source to be consistent — MVT's winding convention is
 * defined in a y-down space and is easy to reason about incorrectly — normalise
 * it here. After this, wall winding is a fixed choice rather than a bet.
 */
function orientRings(rings: Float64Array[]): Float64Array[] {
  return rings.map((ring, index) => {
    const area = signedRingArea(ring);
    const wantPositive = index === 0;
    return area < 0 === wantPositive ? reversed(ring) : ring;
  });
}

export function buildBuildings(footprints: BuildingFootprint[]): BuildingGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const ranges: number[] = [];
  const slots: number[] = [];
  const shades: number[] = [];

  for (const raw of footprints) {
    // A degenerate footprint still gets its (empty) range, so ranges stay
    // aligned one-to-one with the input and callers can pair them by index.
    if (raw.rings[0] === undefined || raw.rings[0].length < 6) {
      ranges.push(positions.length / 3, 0);
      continue;
    }
    const footprint: BuildingFootprint = {
      rings: orientRings(raw.rings),
      height: raw.height,
    };

    const startVertex = positions.length / 3;
    const top = footprint.height;

    // --- roof ---------------------------------------------------------------
    const coords: number[] = [];
    const holes: number[] = [];
    for (let r = 0; r < footprint.rings.length; r++) {
      if (r > 0) holes.push(coords.length / 2);
      const ring = footprint.rings[r];
      for (let i = 0; i < ring.length; i += 2) coords.push(ring[i], ring[i + 1]);
    }

    const roof = earcut(coords, holes, 2);
    if (roof.length === 0) {
      ranges.push(startVertex, 0);
      continue;
    }

    const roofBase = positions.length / 3;
    for (let i = 0; i < coords.length; i += 2) {
      positions.push(coords[i], top, coords[i + 1]);
      slots.push(SLOT_ROOF);
      shades.push(SHADE_FULL);
    }
    // earcut emits triangles wound for a y-down 2D plane. Read in x/z with Y
    // up that is clockwise from above, which faces the roof downward — so the
    // winding is reversed here.
    for (let i = 0; i + 2 < roof.length; i += 3) {
      indices.push(roofBase + roof[i], roofBase + roof[i + 2], roofBase + roof[i + 1]);
    }

    // --- walls --------------------------------------------------------------
    for (const ring of footprint.rings) {
      const points = ring.length / 2;
      for (let i = 0; i < points; i++) {
        const j = (i + 1) % points;
        const x0 = ring[i * 2];
        const z0 = ring[i * 2 + 1];
        const x1 = ring[j * 2];
        const z1 = ring[j * 2 + 1];
        if (x0 === x1 && z0 === z1) continue;

        const v = positions.length / 3;
        positions.push(x0, 0, z0, x1, 0, z1, x1, top, z1, x0, top, z0);
        slots.push(SLOT_WALL, SLOT_WALL, SLOT_WALL, SLOT_WALL);
        shades.push(SHADE_WALL_BASE, SHADE_WALL_BASE, SHADE_WALL_TOP, SHADE_WALL_TOP);
        // Reversed relative to the obvious order: with the outer ring
        // normalised to positive winding, this is what points the wall away
        // from the building's interior. Asserted by a test.
        indices.push(v, v + 2, v + 1, v, v + 3, v + 2);
      }
    }

    ranges.push(startVertex, positions.length / 3 - startVertex);
  }

  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    ranges: new Uint32Array(ranges),
    slots: new Uint8Array(slots),
    shades: new Uint8Array(shades),
  };
}

/** Area of a ring given as flat x,z pairs. */
export function ringArea(ring: Float64Array): number {
  let sum = 0;
  const count = ring.length / 2;
  for (let i = 0, j = count - 1; i < count; j = i++) {
    sum += (ring[j * 2] - ring[i * 2]) * (ring[j * 2 + 1] + ring[i * 2 + 1]);
  }
  return Math.abs(sum) / 2;
}
